import {
  Directive,
  forwardRef,
  input,
  InputSignal,
  effect,
  inject
} from '@angular/core';
import {
  NG_VALIDATORS
} from '@angular/forms';
import { NumberParserService } from '../../../services/number/number-parser.service';

import type {
  Provider,
  Type
} from '@angular/core';
import type {
  AbstractControl,
  ValidationErrors,
  Validator,
  ValidatorFn
} from '@angular/forms';

export const MAX_VALUE_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef((): Type<MaxValueValidator> => MaxValueValidator),
  multi: true
};

/**
 * MaxValueValidator Directive
 *
 * Validates number input if greater than the specified value
 *
 * @example
 * <input type="number" max="100"/>
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[max][formControlName],[max][formControl],[max][ngModel]',
  providers: [MAX_VALUE_VALIDATOR]
})
export class MaxValueValidator implements Validator {
  // underlying input signal (alias preserves attribute binding)
  readonly maxSignal: InputSignal<number | undefined> = input<number | undefined>(undefined, { alias: 'max' });
  // override storage for programmatic assignments (tests, runtime setters)
  private _maxOverride?: number;
  // expose numeric property for compatibility with existing code/tests
  get max(): number | undefined { return this._maxOverride ?? this.maxSignal(); }
  set max(v: number | undefined) {
    this._maxOverride = v;
    if (v !== undefined) {
      this.createValidator(v);
      if (this.validatorOnChange !== undefined) {
        this.validatorOnChange();
      }
    }
  }

  // neutral validator so validate() is safe before inputs initialize
  private validatorFn: ValidatorFn = () => null;
  private validatorOnChange?: () => void;
  private readonly numberParserService: NumberParserService = inject(NumberParserService);

  constructor() {
    effect(() => {
      const m = this.maxSignal();
      if (m !== undefined) {
        this.createValidator(m);
        if (this.validatorOnChange !== undefined) {
          this.validatorOnChange();
        }
      }
    });
  }

  /**
   * max
   *
   * Static method to allow method to be accessed by reactive forms
   *
   * @param maxValue
   * @param numberParserService
   * @returns
   */
  static max(maxValue: number, numberParserService: NumberParserService): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const actualValue: number = numberParserService.parse(`${control.value}`);
      return !isNaN(actualValue) && actualValue > maxValue ? { max : { maxValue, actualValue } } : null;
    };
  }

  /**
   * See implements Validator.validate
   *
   * @param abstractControl
   * @returns ValidationErrors
   */
  validate(abstractControl: AbstractControl): ValidationErrors | null {
    return this.validatorFn(abstractControl);
  }

  /**
   * See Validator.registerOnValidatorChange
   *
   * @param validatorOnChange
   */
  registerOnValidatorChange(validatorOnChange: () => void): void {
    this.validatorOnChange = validatorOnChange;
  }

  validateValue(value: unknown): boolean {
    const actualValue: number = this.numberParserService.parse(String(value));
    if (isNaN(actualValue)) {
      return false;
    }
    const max = this.max;
    if (max === undefined) {
      // no max constraint -> value is considered valid
      return true;
    }
    return actualValue <= max;
  }

  private createValidator(maxValue: number): void {
    this.validatorFn = MaxValueValidator.max(maxValue, this.numberParserService);
  }
}
